/**
 * Gallery lightbox adapter — give the PARENT product-image lightbox the unified
 * close UX (Escape / browser-Back) WITHOUT forking the parent gallery.
 *
 * What the lightbox is (verified on staging): the Snackys parent's
 * `#gallery.zoom` body-class overlay, driven by `js/snackys/jtl.gallery.js`.
 * Clicking `#gallery a` calls `GalleryClass.lightbox()` which toggles
 * `body.lightbox-shown` and re-renders `#gallery` with big images in place;
 * `#close-lightbox` removes the class; hover-zoom is the jQuery `zoom` plugin;
 * switching is thumbnails + swiper. It is **NOT a Bootstrap modal** — no `.modal`,
 * no `data-dismiss`, no `*.bs.modal` lifecycle — so the engine + the
 * `shown.bs.modal` upgrade adapter never see it.
 *
 * Why an adapter and not a migration (ADR-0008 D4 "light-touch"): migrating the
 * gallery onto `.datte-modal` would fork the parent's deeply-integrated
 * zoom/thumbnails/swipe — a large speculative refactor with feature-regression
 * and parent-update risk. Instead this adapter layers ONLY the missing close UX
 * on top of the parent's existing state, delegating the actual close to the
 * parent's own mechanism (`#close-lightbox`).
 *
 * Mechanism (SRP, additive, idempotent):
 *   - Open-state is read from `body.lightbox-shown` (the parent's single source
 *     of truth) — never re-derived.
 *   - A `MutationObserver` on `<body>`'s class turns the parent's open/close into
 *     `open()`/`closed()` signals for the SHARED `setupHistoryBackToClose`
 *     primitive (the same back-to-close state machine the `.datte-modal` stack
 *     uses — no second implementation).
 *   - Escape is wired via the SHARED `setupEscapeToClose` primitive, guarded by
 *     the same open-state predicate.
 *   - Close is delegated to clicking `#close-lightbox` (the parent's own close
 *     path, jtl.gallery.js:248) — this adapter NEVER manipulates the gallery DOM
 *     or removes the body class itself, so the parent stays the single owner of
 *     open/close/render.
 *   - Optional backdrop-close: a click on the dark frame AROUND the active image
 *     closes it. Scoped to `#gallery > a.active` padding/border area only, and a
 *     no-op for clicks on the image itself or on the prev/next arrows, so it never
 *     fights zoom (the image) or swipe (the arrows).
 *
 * Boots once (guarded), tolerant of the parent re-rendering `#gallery`: it binds
 * a single delegated body-level Escape listener + a single body MutationObserver,
 * neither of which is torn down by the gallery's internal re-render.
 *
 * @see docs/architecture/modals/jtl-modal-inventory.md §D4
 * @see src-components/modal/overlay-close-driver.ts (shared primitives)
 */

import {
  setupEscapeToClose,
  setupHistoryBackToClose,
  type HistoryBackToClose,
} from './overlay-close-driver';

/** The parent's open-state flag — the single source of truth for "lightbox open". */
const OPEN_CLASS = 'lightbox-shown';
/** The parent's own close control (jtl.gallery.js binds its click to remove OPEN_CLASS). */
const CLOSE_SELECTOR = '#close-lightbox';
/** Marks the body so a second boot() is a no-op even across partial re-includes. */
const BOOTED_FLAG = 'datteGalleryLightboxBound';

/** True while the parent lightbox overlay is open. Pure read of the parent's flag. */
export function isLightboxOpen(): boolean {
  return document.body.classList.contains(OPEN_CLASS);
}

/**
 * Close the lightbox the parent's way: click `#close-lightbox`. We click the
 * native control rather than removing the class ourselves so the parent's own
 * handler runs (it also re-renders `#gallery` back to the inline thumbnail
 * state — removing the class alone would skip that). No-op if the control is
 * absent (defensive; it is always present on a detail page).
 */
export function closeLightbox(): void {
  document.querySelector<HTMLElement>(CLOSE_SELECTOR)?.click();
}

/**
 * Whether a click on the open lightbox should be treated as a "backdrop" close:
 * true only when the target is the dark frame around the active image — i.e.
 * inside `#gallery` but NOT on the image, a thumbnail, a video, or a prev/next
 * arrow (which own their own click behaviour). Pure predicate — exported for tests.
 *
 * Conservative by design: anything interactive (img, a, button, the arrows, the
 * thumbnails strip) is excluded so backdrop-close never swallows a zoom or swipe.
 */
export function isBackdropClick(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  const gallery = document.getElementById('gallery');
  if (!gallery || !gallery.contains(target)) return false;
  // Exclude the image and every interactive descendant the gallery owns.
  if (
    target.closest(
      'img, button, .sl-ar, #gallery-prev, #gallery-next, #gallery-thumbs, .gal-video, .has-video'
    )
  ) {
    return false;
  }
  return true;
}

/**
 * Boot the adapter. Idempotent (guarded by the body flag) and safe to call before
 * or after jtl.gallery.js has initialised — it only observes the parent's state.
 * Returns true when it bound this call, false when it was already booted.
 */
export function initGalleryLightboxAdapter(): boolean {
  const body = document.body;
  if (body.dataset[BOOTED_FLAG] === 'true') return false;

  // No gallery on this page → nothing to adapt. (#close-lightbox lives in the
  // parent's productdetails/image.tpl, present only on detail pages.)
  if (!document.querySelector(CLOSE_SELECTOR)) return false;

  body.dataset[BOOTED_FLAG] = 'true';

  // --- back-to-close: shared state machine, driven by the body-class observer ---
  const backToClose: HistoryBackToClose = setupHistoryBackToClose(closeLightbox, {
    datteOverlay: 'gallery-lightbox',
  });

  // The parent toggles OPEN_CLASS on <body>; translate that into open/closed signals.
  let wasOpen = isLightboxOpen();
  const observer = new MutationObserver(() => {
    const open = isLightboxOpen();
    if (open === wasOpen) return;
    wasOpen = open;
    if (open) backToClose.open();
    else backToClose.closed();
  });
  observer.observe(body, { attributes: true, attributeFilter: ['class'] });

  // --- Escape: shared primitive, guarded by the parent's open-state ---
  const unbindEscape = setupEscapeToClose(isLightboxOpen, closeLightbox);

  // --- optional backdrop-close: delegated, conservative (never fights zoom/swipe) ---
  const onBodyClick = (e: MouseEvent) => {
    if (!isLightboxOpen()) return;
    if (isBackdropClick(e.target)) closeLightbox();
  };
  body.addEventListener('click', onBodyClick);

  // Teardown is rarely needed in production (a detail page full-reloads), but it
  // keeps the adapter symmetric and lets tests reset between cases without leaking
  // document/body listeners across boots.
  teardown = () => {
    observer.disconnect();
    unbindEscape();
    body.removeEventListener('click', onBodyClick);
    backToClose.destroy();
    delete body.dataset[BOOTED_FLAG];
    teardown = null;
  };

  return true;
}

/** Module-level teardown for the current boot; null when not booted. Test/SPA hook. */
let teardown: (() => void) | null = null;

/** Tear down the current adapter boot (idempotent). Mainly for tests / SPA resets. */
export function teardownGalleryLightboxAdapter(): void {
  teardown?.();
}
